import React, { Component, ReactNode } from 'react';
import CustomTable from '../../../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import { query, repository, subscribe } from '@trlogic/client-js';
import { CollectionListField, List } from '../../../../domain/list/ListDomains';
import CustomLoading from '../../../custom/CustomLoading';
import CollectionNames from '../../../../constant/CollectionNames';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import { Calendar, CalendarChangeParams } from 'primereact/calendar';
import moment from 'moment';
import CustomInput from '../../../custom/CustomInput';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import ListDataService from '../../../../service/ListDataService';
import { TransactionData } from '../../../../domain/stream/transaction/TransactionDomains';
import EmptyComponent from '../../../custom/empty-components/EmptyComponent';
import ComponentName from '../../../../constant/ComponentName';
import { CaseEventReference, CaseSettings } from '../../../../domain/case/CaseDomains';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../custom/FormicaSearchSelectBox';
import FormicaLabel from '../../../custom/FormicaLabel';
import { AnimatedButton } from '@trlogic/ui-pack';
import Subscription = subscribe.Subscription;
import Computation = subscribe.Computation;
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import and = query.and;
import inside = query.inside;
import LanguageUtil from '../../../../util/LanguageUtil';

class CaseListModalComponent extends Component<Props, State> {
  public state: State = new State();

  private listSubscription: Subscription | undefined;
  private computation: Computation | undefined;

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private listService: ListDataService = ServiceContainer.getInstance().get(ServiceNames.LIST_DATA);

  public render(): ReactNode {
    const { disabled } = this.props;
    const { loading, lists, selectedList, listValues, selectedListValues } = this.state;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex w-full h-full rounded-lg mx-5'}>
        {lists.length == 0 ? (
          <EmptyComponent type={ComponentName.LIST} />
        ) : (
          <div className={'flex relative flex-col gap-y-5 w-full h-full bg-white items-start justify-center py-3'}>
            <div className={'flex w-full gap-x-5 items-center justify-between'}>
              <span className='inline-flex flex-col w-1/3'>
                <FormicaLabel label={LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' })} />
                <FormicaSearchSelectBox
                  value={selectedList}
                  icon={'list'}
                  rounded
                  disabled={disabled}
                  placeholder={LanguageUtil.get({ id: 'common.input.placeholder.list', defaultMessage: 'List' })}
                  options={this.getListOptions()}
                  onChange={this.onChangeList}
                />
              </span>
              <div className={'flex items-end justify-end  gap-x-5 gap-y-5 flex-wrap lg:flex-nowrap'}>
                <AnimatedButton
                  icon={'delete'}
                  text={LanguageUtil.get({ id: 'common.button.remove', defaultMessage: 'Remove' })}
                  customType={'secondary'}
                  onClick={this.removeFromList}
                  disabled={disabled}
                />
                <AnimatedButton
                  icon={'add'}
                  text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
                  customType={'outlined'}
                  onClick={this.addToList}
                  disabled={disabled}
                />
              </div>
            </div>

            <div className='flex w-full max-h-[400px] overflow-y-scroll h-full items-start justify-start'>
              {listValues?.length <= 0 && <EmptyComponent createDisabled={true} type={ComponentName.LIST} />}
              {listValues?.length > 0 && (
                <CustomTable
                  columns={this.getColumns()}
                  selectionMode='checkbox'
                  value={listValues}
                  selection={selectedListValues}
                  onSelectionChange={this.onSelectionChange}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  public componentDidMount(): void {
    if (!this.props.disabled) this.startTracker();
  }

  public componentWillUnmount(): void {
    this.stopTracker();
  }

  private startTracker = () => {
    const { domain } = this.props;
    const listQuery = QueryBuilder.select()
      .from(CollectionNames.LISTS)
      .where(and(eq('type', 'INTERNAL_LIST'), inside('id', domain.blacklists)))
      .select('id', 'name', 'type')
      .build();
    this.listSubscription = subscribe.Formica.subscribe(CollectionNames.LISTS, listQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.listSubscription]);
  };

  private stopTracker = async () => {
    await this.listSubscription?.stop();
    this.computation?.stop();
  };

  private tracker = () => {
    if (this.listSubscription?.ready()) {
      this.buildData();
    }
  };

  private buildData = async () => {
    const { domain } = this.props;
    const listQuery = QueryBuilder.select()
      .from(CollectionNames.LISTS)
      .where(and(eq('type', 'INTERNAL_LIST'), inside('id', domain.blacklists)))
      .select('id', 'name')
      .build();
    const lists: string[] = (await this.repository.findMany<List>(listQuery)).flatMap((l: List) => l.name);
    this.setState({ lists, loading: false });
  };

  private getListOptions = (): FormicaSelectBoxItem[] => {
    const { lists } = this.state;
    return lists.map((l) => ({
      label: l,
      value: l,
      title: l,
      children: [],
    }));
  };

  private buildValues = () => {
    const { transactionData } = this.props;
    const listValues: (CollectionListField & { variable: string })[] = Object.entries(transactionData.variables)
      .filter(([_, value]) => typeof value == 'string' || typeof value == 'number' || typeof value == 'boolean')
      .map(([key, value]) => {
        return {
          variable: key,
          expireAt: moment().add('5', 'year').valueOf(),
          comment: '',
          value: value,
        };
      });
    this.setState({ listValues });
  };

  private onChangeComment = (e: React.ChangeEvent<HTMLInputElement>, record: CollectionListField) => {
    const { listValues } = this.state;
    record.comment = e.target.value;
    this.setState({ listValues });
  };

  private onChangeExpireDate = (e: CalendarChangeParams, record: CollectionListField) => {
    const value: any = e.value instanceof Date ? e.value.valueOf() : typeof e.value == 'string' ? new Date(e.value).valueOf() : e.value;
    const { listValues } = this.state;
    if (value) {
      record.expireAt = moment(value).valueOf();
      this.setState({ listValues });
    }
  };

  private onSelectionChange = (e: any) => {
    this.setState({ selectedListValues: e.value });
  };

  private addToList = async () => {
    const { selectedListValues, selectedList } = this.state;
    const { disabled } = this.props;
    if (selectedListValues.length == 0 || selectedList == undefined || disabled) {
      return;
    }
    try {
      for (const l of selectedListValues.map((s, i) => ({
        item: s.value,
        description: s.comment,
        expireAt: s.expireAt,
        createdAt: moment().valueOf(),
        updatedAt: moment().valueOf(),
      }))) {
        await this.listService.addData(selectedList, 'item', l, true, this.getCaseReference());
      }
      this.setState({ selectedListValues: [] });
    } catch (e) {}
  };

  private getCaseReference = (): CaseEventReference => {
    const { transactionData, domain } = this.props;
    return { transactionId: transactionData.id, domain: domain.name };
  };

  private removeFromList = async () => {
    const { selectedListValues, selectedList } = this.state;
    const { disabled } = this.props;
    if (selectedListValues.length == 0 || selectedList == undefined || disabled) {
      return;
    }
    try {
      for (const l of selectedListValues.map((s) => ({
        value: s.value,
      }))) {
        await this.listService.deleteListData(selectedList, l.value, this.getCaseReference());
      }
      this.setState({ selectedListValues: [] });
    } catch (e) {}
  };

  private getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'variable',
        header: LanguageUtil.get({ id: 'pages.case.close.table.variable', defaultMessage: 'Variable' }),
        alignHeader: 'left',
        columnKey: 'variable',
        body: (record: CollectionListField & { variable: string }) => {
          return <div className={'flex items-start text-f-black-3'}>{record.variable}</div>;
        },
      },
      {
        field: 'value',
        header: LanguageUtil.get({ id: 'pages.case.close.table.value', defaultMessage: 'Value' }),
        alignHeader: 'left',
        columnKey: 'value',
        body: (record: CollectionListField) => {
          return <div className={'flex items-start text-f-black-3'}>{record.value}</div>;
        },
      },
      {
        field: 'comment',
        header: LanguageUtil.get({ id: 'pages.case.close.table.comment', defaultMessage: 'Comment' }),
        alignHeader: 'left',
        columnKey: 'comment',
        body: (record: CollectionListField) => {
          return (
            <div className={'flex items-start text-f-black-3'}>
              <CustomInput
                inputType={'ghost'}
                type={'text'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onChangeComment(e, record)}
                value={record.comment}
              />
            </div>
          );
        },
      },
      {
        field: 'expireAt',
        header: LanguageUtil.get({ id: 'pages.case.close.table.expireAt', defaultMessage: 'Expire At' }),
        alignHeader: 'left',
        columnKey: 'expire',
        body: (record: CollectionListField) => {
          return (
            <div className={'text-f-black-3'}>
              <Calendar
                className={'!h-[34px]'}
                showTime
                hourFormat='24'
                value={new Date(record.expireAt)}
                onChange={(e: CalendarChangeParams) => this.onChangeExpireDate(e, record)}
              />
            </div>
          );
        },
      },
    ];
  };

  private onChangeList = (e: FormicaSelectBoxItem) => {
    this.setState({ selectedList: e.value }, this.buildValues);
  };
}

interface Props {
  transactionData: TransactionData;
  disabled: boolean;
  domain: CaseSettings;
}

class State {
  loading: boolean = true;
  lists: string[] = [];
  selectedList: string | undefined;
  listValues: (CollectionListField & { variable: string })[] = [];
  selectedListValues: (CollectionListField & { variable: string })[] = [];
}

export default CaseListModalComponent;
